import { useState } from "react";
import { NotebookPen, Plus, Trash2 } from "lucide-react";
import PageShell from "../components/PageShell";
import { getSermons, addSermon, deleteSermon } from "../lib/personal";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

export default function SermonNotes() {
  const [items, setItems] = useState(getSermons());
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", speaker: "", date: "", passage: "", notes: "" });
  const refresh = () => setItems(getSermons());

  const save = () => {
    if (!form.title.trim()) return;
    addSermon({ ...form, title: form.title.trim() });
    setForm({ title: "", speaker: "", date: "", passage: "", notes: "" });
    setOpen(false);
    refresh();
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <PageShell title="Sermon Notes" subtitle={`${items.length} messages captured`} icon={NotebookPen}
      accent={{ a: "#6E8FB0", b: "#5C7A8A", c: "#384C66" }} testid="notes-page">
      <div className="mb-6 flex justify-end">
        <button onClick={() => setOpen(true)} data-testid="add-sermon"
          className="font-ui flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-transform hover:scale-105">
          <Plus className="h-4 w-4" /> New Note
        </button>
      </div>

      {items.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-border bg-background/30 p-12 text-center">
          <NotebookPen className="mx-auto mb-3 h-8 w-8 text-primary/60" />
          <p className="font-ui text-sm text-muted-foreground">Capture your first sermon — speaker, passage, and takeaways.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((s) => (
            <div key={s.id} data-testid={`sermon-${s.id}`} className="rounded-2xl border border-border bg-background/40 p-5 backdrop-blur-md">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-serif-display text-xl">{s.title}</h3>
                  <p className="font-ui mt-1 text-xs text-muted-foreground">
                    {[s.speaker, s.date, s.passage].filter(Boolean).join(" · ")}
                  </p>
                </div>
                <button onClick={() => { deleteSermon(s.id); refresh(); }} data-testid={`delete-sermon-${s.id}`}
                  className="flex h-8 w-8 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors hover:bg-muted">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              {s.notes && <p className="font-ui mt-3 whitespace-pre-wrap text-sm leading-relaxed text-foreground/80">{s.notes}</p>}
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="font-ui max-h-[88vh] overflow-y-auto" data-testid="sermon-dialog">
          <DialogHeader><DialogTitle className="font-serif-display text-2xl">New Sermon Note</DialogTitle></DialogHeader>
          <Input data-testid="sermon-title" placeholder="Sermon title" value={form.title} onChange={set("title")} />
          <div className="grid grid-cols-2 gap-3">
            <Input data-testid="sermon-speaker" placeholder="Speaker" value={form.speaker} onChange={set("speaker")} />
            <Input data-testid="sermon-date" type="date" value={form.date} onChange={set("date")} />
          </div>
          <Input data-testid="sermon-passage" placeholder="Passage (e.g. Romans 8:28)" value={form.passage} onChange={set("passage")} />
          <Textarea data-testid="sermon-notes" placeholder="Your notes…" value={form.notes} onChange={set("notes")} rows={7} />
          <button onClick={save} data-testid="save-sermon" className="mt-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-transform hover:scale-[1.02]">
            Save Note
          </button>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
